import { useState, useEffect, useCallback } from 'react';
import { learnerService } from '../services/learnerService';

export interface SkillGraphNode {
  id: string;
  code: string;
  name: string;
  subject: string;
  gradeLevel: number;
}

export type SkillGraphEdge = [string, string]; // [prerequisiteId, dependentId]

export interface UseSkillGraphReturn {
  nodes: SkillGraphNode[];
  edges: SkillGraphEdge[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * Fetches the active skill graph (GET /api/skills/graph, Phase 2). Not
 * learner-scoped - this is the deterministic graph shared by every child,
 * one call cached for the session rather than refetched per skill.
 */
export const useSkillGraph = (): UseSkillGraphReturn => {
  const [nodes, setNodes] = useState<SkillGraphNode[]>([]);
  const [edges, setEdges] = useState<SkillGraphEdge[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchGraph = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;

    setLoading(true);
    setError(null);
    try {
      const data = await learnerService.getSkillGraph(token);
      setNodes(data.nodes || []);
      setEdges(data.edges || []);
    } catch (err) {
      console.error('Error fetching skill graph:', err);
      setError('Failed to load skill graph');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGraph();
  }, [fetchGraph]);

  return { nodes, edges, loading, error, refetch: fetchGraph };
};
